import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useSelector } from 'react-redux';
import List from '@mui/material/List';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemText from '@mui/material/ListItemText';
import Collapse from '@mui/material/Collapse';
import Divider from '@mui/material/Divider';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import Drawer from '@mui/material/Drawer';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import adminMenu from '../../config/adminMenu';
import { fetchPublicFeatureFlags } from '../../services/featureFlagsApi';
import { canAccessStaffMenuItem, resolveStaffRoles } from '../../utils/staffRoles';

const AdminSidebar = ({ collapsedInitially = true, topOffset = 0, mobileOpen = false, onMobileClose = () => { } }) => {
  // topOffset: pixels to push the sidebar down (useful when top navbar is fixed)
  const [openGroups, setOpenGroups] = React.useState(() => {
    const map = {};
    adminMenu.groups.forEach(g => { map[g.id] = !collapsedInitially; });
    return map;
  });

  const navigate = useNavigate();
  const { logout } = useAuth();

  const toggleGroup = (id) => {
    setOpenGroups(prev => ({ ...prev, [id]: !prev[id] }));
  };

  const handleLogout = async () => {
    try {
      await logout();
    } finally {
      navigate('/admin/login');
    }
  };

  const theme = useTheme();
  // Show the persistent left sidebar only on wide screens (>1200px).
  // For widths <= 1200px use the mobile Drawer (hamburger) navigation.
  const isDesktop = useMediaQuery('(min-width:1201px)');
  // convenience flag used in click handlers to close the mobile drawer
  const isMobile = !isDesktop;

  const authState = useSelector((state) => state.auth);
  const roles = resolveStaffRoles(authState);
  const [featureFlags, setFeatureFlags] = React.useState([]);

  React.useEffect(() => {
    let mounted = true;

    (async () => {
      try {
        const flags = await fetchPublicFeatureFlags();
        if (mounted) {
          setFeatureFlags(Array.isArray(flags) ? flags : []);
        }
      } catch {
        if (mounted) {
          setFeatureFlags([]);
        }
      }
    })();

    return () => {
      mounted = false;
    };
  }, []);
  const sidebarContent = (
    <Box sx={{
      width: 260,
      // Two-tone Accent Column (option C)
      backgroundColor: '#1e2225',
      color: '#E8F1F4',
      borderRight: '1px solid rgba(255,255,255,0.08)',
      minHeight: '100vh',
      paddingTop: `${12 + Number(topOffset || 0)}px`,
      boxSizing: 'border-box',
      position: 'relative',
      // slight inner separation so dividers are more noticeable
      boxShadow: 'inset -1px 0 0 rgba(255,255,255,0.02)'
    }}>
      <List component="nav" sx={{ p: 0 }}>
        {adminMenu.groups.map((group, idx) => (
          <React.Fragment key={group.id}><Box key={group.id}>
            <ListItemButton
              onClick={() => toggleGroup(group.id)}
              sx={{
                px: 2,
                color: '#E8F1F4',
                '&:hover': { backgroundColor: 'rgba(255,255,255,0.03)' },
                '&:focus-visible': { boxShadow: '0 0 0 3px rgba(255,209,102,0.06)' }
              }}
            >
              <ListItemText primary={group.label} primaryTypographyProps={{ fontWeight: 600, color: '#E8F1F4' }} />
              {openGroups[group.id] ? <ExpandLessIcon fontSize="small" sx={{ color: 'inherit' }} /> : <ExpandMoreIcon fontSize="small" sx={{ color: 'inherit' }} />}
            </ListItemButton>

            <Collapse in={openGroups[group.id]} timeout="auto" unmountOnExit>
              <List component="div" disablePadding>
                {group.items.map(item => {
                  if (!canAccessStaffMenuItem(item, roles, featureFlags)) return null;
                  // Remove preview menu item (not a real page)
                  if (item.to === '/admin/public-user/preview') return null;
                  return (
                    <ListItemButton
                      key={item.to}
                      component={Link}
                      to={item.to}
                      onClick={(e) => {
                        if (isMobile) onMobileClose();
                      }}
                      sx={{
                        pl: 4,
                        py: 1.1,
                        color: '#E8F1F4',
                        backgroundColor: 'transparent',
                        '&:hover': { backgroundColor: 'rgba(255,255,255,0.03)' },
                        '&.active': {
                          backgroundColor: 'rgba(255,209,102,0.06)',
                          color: '#ffd166',
                          borderLeft: '4px solid #ffd166',
                          paddingLeft: 'calc(16px)'
                        },
                        '&:focus-visible': { boxShadow: '0 0 0 3px rgba(255,209,102,0.06)' }
                      }}
                    >
                      <ListItemText primary={item.label} primaryTypographyProps={{ fontSize: '0.95rem', color: '#E8F1F4' }} />
                    </ListItemButton>
                  );
                })}
              </List>
            </Collapse>
          </Box>
            {idx < adminMenu.groups.length - 1 && (
              <Divider sx={{ borderColor: 'rgba(255,255,255,0.08)', my: 0.5 }} />
            )}
          </React.Fragment>
        ))}
      </List>
      <Divider sx={{ borderColor: 'rgba(255,255,255,0.12)', mt: 1 }} />
      <Box sx={{ px: 1, py: 1 }}>
        <ListItemButton onClick={async () => { await handleLogout(); if (isMobile) onMobileClose(); }} sx={{ px: 2, color: '#E8F1F4', '&:hover': { backgroundColor: '#1c2024' }, '&:focus-visible': { boxShadow: '0 0 0 3px rgba(255,140,66,0.08)' } }}>
          <ListItemText primary="Logout" primaryTypographyProps={{ fontSize: '0.95rem', color: '#E8F1F4' }} />
        </ListItemButton>
      </Box>
    </Box>
  );

  if (!isDesktop) {
    // Mobile/tablet: show Drawer controlled by hamburger
    return (
      <Drawer
        anchor="left"
        open={!!mobileOpen}
        onClose={onMobileClose}
        ModalProps={{ keepMounted: true }}
        PaperProps={{ sx: { width: 260, bgcolor: '#1e2225', color: '#E8F1F4' } }}
      >
        {sidebarContent}
      </Drawer>
    );
  }

  // Desktop (wide): render persistent left sidebar
  return sidebarContent;
};

export default AdminSidebar;
